import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';


import { QRAnalyticsService } from '../../../services/qrAnalyticsService';

const TopPerformingQRs = ({ qrCodes = [], loading = false, onQRClick, showStylingInfo = false }) => {
  const navigate = useNavigate();
  const [localData, setLocalData] = useState([]);
  const [localLoading, setLocalLoading] = useState(false);
  const [error, setError] = useState('');
  const [topQRs, setTopQRs] = useState([]);

  // Load QR performance data when component mounts
  useEffect(() => {
    const loadTopPerformingQRs = async () => {
      setLocalLoading(true);
      setError('');

      try {
        // FIXED: Call method with correct parameters (dateRange, campaignId, limit)
        const qrData = await QRAnalyticsService?.getTopPerformingQRs('last7d', null, 5);
        
        if (!qrData || (Array.isArray(qrData) && qrData?.length === 0)) {
          setError('No QR codes found in database');
          setTopQRs([]); // Empty array for "No QR Codes Found" state
        } else {
          setTopQRs(qrData || []);
        }
      } catch (error) {
        setError(`Failed to load top performing QR codes: ${error?.message}`);
        setTopQRs([]); // Empty array for error state
      } finally {
        setLocalLoading(false);
      }
    };

    loadTopPerformingQRs();
  }, []);

  const showLoading = loading || localLoading;
  const displayData = topQRs || [];

  const handleQRClick = (qr) => {
    if (onQRClick) {
      onQRClick(qr);
    } else {
      // Navigate to QR code details or library
      navigate(`/qr-code-library`);
    }
  };

  const handleViewAll = () => {
    navigate('/qr-code-library');
  };

  const getQRTypeIcon = (type) => {
    const typeIcons = {
      'url': 'Globe',
      'vcard': 'User',
      'whatsapp': 'MessageCircle',
      'email': 'Mail',
      'sms': 'MessageSquare',
      'wifi': 'Wifi',
      'text': 'FileText',
      'phone': 'Phone'
    };
    return typeIcons?.[type] || 'QrCode';
  };

  const getQRTypeLabel = (type) => {
    const typeLabels = {
      'url': 'Website',
      'vcard': 'Contact',
      'whatsapp': 'WhatsApp',
      'email': 'Email',
      'sms': 'SMS',
      'wifi': 'WiFi',
      'text': 'Text',
      'phone': 'Phone'
    };
    return typeLabels?.[type] || 'QR Code';
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Performing QR Codes</h3>
      {loading ? (
        <div className="space-y-3">
          {[...Array(3)]?.map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gray-200 rounded"></div>
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2 mt-1"></div>
                </div>
                <div className="text-right">
                  <div className="h-4 bg-gray-200 rounded w-12"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : qrCodes?.length === 0 ? (
        <div className="text-center py-8">
          <div className="text-gray-400 mb-2">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <p className="text-gray-500">No QR code analytics yet</p>
          <p className="text-sm text-gray-400 mt-1">Create QR codes and generate scans to see performance data</p>
        </div>
      ) : (
        <div className="space-y-4">
          {qrCodes?.map((qr, index) => (
            <div 
              key={qr?.id || index} 
              className={`flex items-center justify-between p-3 rounded-lg border hover:bg-gray-50 transition-colors ${
                onQRClick ? 'cursor-pointer' : ''
              }`}
              onClick={() => onQRClick?.(qr)}
            >
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <span className="text-blue-600 font-semibold text-sm">#{index + 1}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {qr?.name || 'Unnamed QR Code'}
                    </p>
                    {/* ADDED: Show styling indicator */}
                    {showStylingInfo && qr?.isStyled && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z" />
                        </svg>
                        Styled
                      </span>
                    )}
                  </div>
                  <div className="flex items-center space-x-4 mt-1">
                    <p className="text-xs text-gray-500 capitalize">{qr?.type || 'url'}</p>
                    {/* ADDED: Show styling details */}
                    {showStylingInfo && qr?.styling && (
                      <div className="flex items-center space-x-1">
                        {qr?.styling?.dotStyle && qr?.styling?.dotStyle !== 'square' && (
                          <span className="text-xs text-purple-600">dots: {qr?.styling?.dotStyle}</span>
                        )}
                        {qr?.styling?.cornerStyle && qr?.styling?.cornerStyle !== 'square' && (
                          <span className="text-xs text-purple-600">corners: {qr?.styling?.cornerStyle}</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm font-semibold text-gray-900">
                  {qr?.scans?.toLocaleString() || '0'} scans
                </p>
                <div className="flex items-center space-x-2 mt-1">
                  <p className="text-xs text-gray-500">
                    {qr?.conversions || 0} conversions
                  </p>
                  {qr?.engagementScore !== undefined && (
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      qr?.engagementScore >= 20 ? 'bg-green-100 text-green-800' :
                      qr?.engagementScore >= 10 ? 'bg-yellow-100 text-yellow-800': 'bg-gray-100 text-gray-800'
                    }`}>
                      {qr?.engagementScore}%
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TopPerformingQRs;